import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AssistantAnswer } from '@aurora-pvi/contracts';
import { useProductList } from '../api/queries';
import { createHttpAssistantRepository } from '../api/httpAssistantRepository';
import { AssistantAnswerPanel } from './AssistantAnswerPanel';
import type { SearchDestination } from './GlobalSearch';

/**
 * Search that happens in the bar rather than over the page.
 *
 * The command palette this replaces was a modal: it dimmed the screen, took
 * focus, and put the results somewhere other than where the search box was.
 * For "find me a product" that is a lot of ceremony, and it hid the screen the
 * reader was searching *from* — which is usually the context for the search.
 *
 * So the field stays where it is, grows to fill the bar, and drops its results
 * directly beneath itself. Nothing else moves.
 *
 * ── The question, in the same box ──────────────────────────────────────────
 * Asking the portfolio a question is the same gesture as searching it, so it is
 * the same field: type something long enough to be a sentence and the first row
 * offers to answer it instead of matching it. The answer lands in the panel
 * that would have held the results, which keeps the promise the rest of this
 * component makes — nothing takes over the screen the reader was working on.
 *
 * Records and destinations stay visibly separate from each other and now from
 * the answer as well, for the reason `GlobalSearch` gives at length: a reader
 * has to be able to tell what is a thing in their estate, what is a place in
 * the app, and what is a sentence somebody's model wrote about both.
 */

const MAX_DESTINATIONS = 5;
const MAX_PRODUCTS = 6;
/** Below this, a query is a search term rather than a question. */
const MIN_QUESTION_LENGTH = 12;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export function InlineSearch({ destinations }: { destinations: SearchDestination[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [asked, setAsked] = useState<AssistantAnswer | null>(null);
  const [askError, setAskError] = useState<string | null>(null);

  const trimmed = query.trim();

  const assistant = useMemo(
    () => (API_BASE_URL ? createHttpAssistantRepository(API_BASE_URL) : null),
    [],
  );

  /*
    Asked once, on mount, and cached for the session.

    Whether a deployment has an assistant does not change while somebody is
    using it, and a question box that appears and disappears is worse than one
    that is honestly absent.
  */
  const availability = useQuery({
    queryKey: ['assistant-availability'],
    queryFn: () => assistant?.availability() ?? Promise.resolve(null),
    enabled: Boolean(assistant),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const ask = useMutation({
    mutationFn: async (question: string) => {
      if (!assistant) throw new Error('No assistant is connected to this environment.');
      const outcome = await assistant.ask(question);
      if (!outcome.ok) throw new Error(outcome.message);
      return outcome.answer;
    },
    onSuccess: (answer) => {
      setAsked(answer);
      setAskError(null);
    },
    onError: (error: unknown) => {
      setAsked(null);
      setAskError(error instanceof Error ? error.message : 'The question was not answered.');
    },
  });

  const canAsk = Boolean(availability.data?.configured) && trimmed.length >= MIN_QUESTION_LENGTH;

  // Products load once the reader has actually typed something. Fetching the
  // catalogue on focus would put a request behind every stray click into the
  // field.
  const productQuery = useProductList();
  const products = trimmed.length >= 2 ? (productQuery.data?.data?.rows ?? []) : [];

  const results = useMemo(() => {
    const q = trimmed.toLowerCase();
    if (!q) return { destinations: [], products: [] };
    return {
      destinations: destinations
        .filter(
          (d) => d.label.toLowerCase().includes(q) || (d.group ?? '').toLowerCase().includes(q),
        )
        .slice(0, MAX_DESTINATIONS),
      products: products
        .filter(
          (p) => p.name.toLowerCase().includes(q) || p.businessCode.toLowerCase().includes(q),
        )
        .slice(0, MAX_PRODUCTS),
    };
  }, [trimmed, destinations, products]);

  const flat = useMemo(
    () => [
      ...(canAsk ? [{ key: 'ask', to: null as string | null }] : []),
      ...results.destinations.map((d) => ({ key: `d:${d.id}`, to: d.to as string | null })),
      ...results.products.map((p) => ({
        key: `p:${p.id}`,
        to: `/v2/products/${p.id}/overview` as string | null,
      })),
    ],
    [results, canAsk],
  );

  // A new query is a new question. Leaving the previous answer under a
  // different search term is how somebody reads an answer to something they did
  // not ask.
  useEffect(() => {
    setActive(0);
    setAsked(null);
    setAskError(null);
  }, [trimmed]);

  // Clicking anywhere else closes the panel. Without this the results hang
  // over the page after the reader has moved on, which is the one behaviour a
  // non-modal search must not have.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // ⌘K still focuses the field, because the shortcut is muscle memory even
  // though there is no longer a palette for it to open.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const go = (to: string) => {
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
    navigate(to);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!flat.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i + 1) % flat.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i - 1 + flat.length) % flat.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = flat[active];
      if (!target) return;
      if (target.to === null) ask.mutate(trimmed);
      else go(target.to);
    }
  };

  const showPanel = open && trimmed.length > 0;

  return (
    <div ref={boxRef} className="relative min-w-0 flex-1">
      <div className="flex items-center gap-2 rounded-[12px] border border-[var(--g-edge)] bg-[var(--g-surface-2)] px-3 py-2 focus-within:border-[var(--g-accent)]">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden className="shrink-0">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search products, applications and screens"
          aria-label="Search"
          className="min-w-0 flex-1 bg-transparent text-[13px] text-[var(--g-text)] outline-none placeholder:text-[var(--g-text-faint)]"
        />
        <kbd className="hidden shrink-0 rounded-[5px] border border-[var(--g-edge)] px-1.5 py-0.5 text-[10px] text-[var(--g-text-faint)] tablet:block">
          ⌘K
        </kbd>
      </div>

      {showPanel ? (
        <div className="g-surface-3 absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-[min(60vh,420px)] overflow-y-auto rounded-[14px] border border-[var(--g-edge)] p-2 shadow-[0_18px_50px_rgba(15,23,42,0.22)]">
          {asked ? (
            <AssistantAnswerPanel answer={asked} onFollow={() => setOpen(false)} />
          ) : askError ? (
            <p className="px-2 py-3 text-[12.5px] leading-relaxed text-[var(--g-text-secondary)]">
              {askError}
            </p>
          ) : ask.isPending ? (
            <p className="px-2 py-3 text-[12.5px] text-[var(--g-text-muted)]">
              Reading the governed views to answer that…
            </p>
          ) : flat.length === 0 ? (
            <p className="px-2 py-3 text-[12.5px] text-[var(--g-text-muted)]">
              Nothing matches “{trimmed}”.
            </p>
          ) : (
            <>
              {canAsk ? (
                <Section title="Ask">
                  <Row
                    active={active === 0}
                    onSelect={() => ask.mutate(trimmed)}
                    primary={`Answer “${trimmed}” from the governed figures`}
                    secondary="Every figure cited"
                  />
                </Section>
              ) : null}
              {results.destinations.length > 0 ? (
                <Section title="Screens">
                  {results.destinations.map((d, index) => (
                    <Row
                      key={d.id}
                      active={active === index + (canAsk ? 1 : 0)}
                      onSelect={() => go(d.to)}
                      primary={d.label}
                      secondary={d.group}
                    />
                  ))}
                </Section>
              ) : null}
              {results.products.length > 0 ? (
                <Section title="Products">
                  {results.products.map((p, index) => (
                    <Row
                      key={p.id}
                      active={active === results.destinations.length + index + (canAsk ? 1 : 0)}
                      onSelect={() => go(`/v2/products/${p.id}/overview`)}
                      primary={p.name}
                      secondary={p.businessCode}
                    />
                  ))}
                </Section>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="px-2 pb-1 pt-1.5 text-[10px] uppercase tracking-[0.08em] text-[var(--g-text-faint)]">
        {title}
      </span>
      {children}
    </div>
  );
}

function Row({
  active,
  onSelect,
  primary,
  secondary,
}: {
  active: boolean;
  onSelect: () => void;
  primary: string;
  secondary: string | null;
}) {
  return (
    <button
      type="button"
      onMouseDown={(event) => {
        // mousedown, not click: the outside-click listener fires first and
        // would close the panel before the click ever lands.
        event.preventDefault();
        onSelect();
      }}
      className={`flex w-full items-baseline justify-between gap-3 rounded-[9px] px-2 py-1.5 text-left ${
        active ? 'bg-[var(--g-surface-2)]' : ''
      }`}
    >
      <span className="truncate text-[13px] text-[var(--g-text)]">{primary}</span>
      {secondary ? (
        <span className="shrink-0 text-[11px] text-[var(--g-text-faint)]">{secondary}</span>
      ) : null}
    </button>
  );
}
